import React, { useContext, useEffect, useState } from "react";
import { Messages, MessageType } from "../../constants/Messages";
import InputMethodTabs from "../shared/InputMethodTabs";
import { loadMaterialMappings } from "../../utils/MaterialsUtils";
import { UpdateMaterialInventoryPayload } from "../../types/InventoryItem";
import { updateInventory } from "../../services/MaterialAPI";
import { UserContext } from "../../context/UserContext";
import {
    catchError,
    formatUniqueKeyCombination,
    handleInventoryResponseAlerts,
    noDataValidation,
    processInvalidRowsForUpdate,
    renderErrorDetails,
} from "../../utils/AlertUtils";
import SharedAlertBanner from "../shared/SharedAlertBanner";
import { BULK_TABS } from "../../constants/TabTypes";
import { FIELD_MAPPINGS } from "../../constants/FiledMappings";
import FormComponent from "../generic/FormComponent";

// Constants for string values
const TABS = BULK_TABS;

const REQUIRED_FIELDS: string[] = [ FIELD_MAPPINGS.MATERIAL_TYPE, FIELD_MAPPINGS.SIZE, FIELD_MAPPINGS.COLOR_CODE, FIELD_MAPPINGS.QUANTITY_UNIT ];

const OPTIONAL_FIELDS: string[] = [
    FIELD_MAPPINGS.IS_NEW,
    FIELD_MAPPINGS.UNIT_PRICE,
    FIELD_MAPPINGS.QUANTITY_CHANGED,
    FIELD_MAPPINGS.REMAINING_QUANTITY,
];

interface BulkUpdateFormProps {
}

const BulkUpdateInventoryForm: React.FC<BulkUpdateFormProps> = () => {
    const [ parsedData, setParsedData ] = useState<any[]>([]);
    const [ invalidRows, setInvalidRows ] = useState<string[]>([]);
    const [ apiData, setApiData ] = useState<any[]>([]);
    const [ rawData, setRawData ] = useState<any[]>([]);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ activeTab, setActiveTab ] = useState<(typeof TABS)[keyof typeof TABS]>(TABS.FILE_UPLOAD);
    const [ materialMappings, setMaterialMappings ] = useState<{
        materialTypeMap: Record<string, number>;
        materialSizeMap: Record<string, number>;
    }>({ materialTypeMap: {}, materialSizeMap: {} });

    const [ fieldMappings, setFieldMappings ] = useState<Record<string, string>>(
        Object.values(FIELD_MAPPINGS).reduce((acc, field) => ({ ...acc, [field]: "" }), {}),
    );

    const [ successAlert, setSuccessAlert ] = useState<{
        message: string;
        type?: MessageType;
        title?: string;
        isHtml?: boolean;
    } | null>(null);

    const [ errorAlert, setErrorAlert ] = useState<{
        message: string;
        type?: MessageType;
        title?: string;
        isHtml?: boolean;
    } | null>(null);

    const { isLoggedIn } = useContext(UserContext);

    useEffect(() => {
        loadMaterialMappings(setMaterialMappings, setErrorAlert);
    }, [ isLoggedIn ]);

    // Re-validate data when fieldMappings change
    useEffect(() => {
        if (rawData.length > 0) {
            validateAndSetDataHandler(rawData);
        }
    }, [ fieldMappings, rawData ]);

    const validateAndSetDataHandler = (data: any[]) => {
        const validRows: any[] = [];
        const invalidRowsList: string[] = [];
        const validApiRows: UpdateMaterialInventoryPayload[] = [];
        const seen = new Set();

        data.forEach((row) => {
            try {
                const parsedRow: Record<string, any> = row;

                const materialTypeId = materialMappings.materialTypeMap[parsedRow[FIELD_MAPPINGS.MATERIAL_TYPE].toLowerCase().trim()];
                const materialSizeId = materialMappings.materialSizeMap[parsedRow[FIELD_MAPPINGS.SIZE].toLowerCase().trim()];

                if (!materialTypeId) throw new Error(Messages.ERROR_MATERIAL_TYPE_NOT_EXIST);
                if (!materialSizeId) throw new Error(Messages.ERROR_MATERIAL_SIZE_NOT_EXIST);

                const quantityChanged = parsedRow[FIELD_MAPPINGS.QUANTITY_CHANGED]
                    ? parseFloat(parsedRow[FIELD_MAPPINGS.QUANTITY_CHANGED])
                    : undefined;
                const remainingQuantity = parsedRow[FIELD_MAPPINGS.REMAINING_QUANTITY]
                    ? parseFloat(parsedRow[FIELD_MAPPINGS.REMAINING_QUANTITY])
                    : undefined;

                if ((quantityChanged && remainingQuantity) || (!quantityChanged && !remainingQuantity)) {
                    throw new Error(Messages.ERROR_QUANTITY_REQUIREMENT);
                }

                const apiData: UpdateMaterialInventoryPayload = {
                    material_type_id: materialTypeId,
                    material_size_id: materialSizeId,
                    color_code: parsedRow[FIELD_MAPPINGS.COLOR_CODE] || "",
                    ...(!Number.isNaN(quantityChanged) && { quantity_changed: quantityChanged }),
                    ...(!Number.isNaN(remainingQuantity) && { remaining_quantity: remainingQuantity }),
                    ...(parsedRow[FIELD_MAPPINGS.IS_NEW] === true || parsedRow[FIELD_MAPPINGS.IS_NEW] === "true"
                        ? { is_new: true }
                        : { is_new: false }),
                    ...(!Number.isNaN(parsedRow[FIELD_MAPPINGS.UNIT_PRICE]) && {
                        unit_price: parseFloat(parsedRow[FIELD_MAPPINGS.UNIT_PRICE]),
                    }),
                };

                const key = formatUniqueKeyCombination(apiData);
                if (seen.has(key)) {
                    throw new Error(Messages.ERROR_DUPLICATE_ENTRY);
                }
                seen.add(key);

                validApiRows.push(apiData);
                validRows.push({ ...parsedRow });
            } catch (error: any) {
                invalidRowsList.push(
                    JSON.stringify({
                        ...row,
                        Reason: error?.message || Messages.UNKNOWN_ERROR,
                    }),
                );
            }
        });

        setApiData(validApiRows);
        setParsedData(validRows);
        setInvalidRows(invalidRowsList);

        renderErrorDetails(invalidRowsList, setErrorAlert);
    };

    const handleDataParsed = (data: any[]) => {
        setParsedData(data);
        setRawData(data);
    };

    const handleFieldMappingChange = (updatedMappings: Record<string, string>) => {
        setFieldMappings(updatedMappings);
    };

    const handleSubmit = async () => {
        noDataValidation(parsedData, setErrorAlert);
        try {
            setIsLoading(true);
            const response = await updateInventory(apiData);
            handleInventoryResponseAlerts(response, setSuccessAlert, setErrorAlert);
            processInvalidRowsForUpdate(response.errors, setParsedData);
            setInvalidRows([]);
            setParsedData([]);
        } catch (error) {
            catchError(error, setErrorAlert);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FormComponent>
            <SharedAlertBanner
                successAlert={successAlert}
                errorAlert={errorAlert}
                onCloseError={() => setErrorAlert(null)}
                onCloseSuccess={() => setSuccessAlert(null)}
            />
            <InputMethodTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onDataParsed={handleDataParsed}
                onSubmit={handleSubmit}
                isLoading={isLoading}
                parsedData={parsedData}
                fieldMappings={fieldMappings}
                requiredFields={REQUIRED_FIELDS as string[]}
                optionalFields={OPTIONAL_FIELDS as string[]}
                onFieldMappingChange={handleFieldMappingChange}
            />
        </FormComponent>
    );
};

export default BulkUpdateInventoryForm;
